import type { ClientLibraryId } from 'types/client-library'
import type { Thunk } from 'types/redux'
import { mapResponseToReducerObjects } from '@disco/state-management/actions/PlaylistTrackActions'
import api from '@disco/api/server'
import {
  CLIENT_LIBRARY_ALBUMS_LOAD,
  CLIENT_LIBRARY_ALBUMS_LOAD_FAIL,
  CLIENT_LIBRARY_ALBUMS_LOAD_NEXT,
  CLIENT_LIBRARY_ALBUMS_LOAD_NEXT_FAIL,
  CLIENT_LIBRARY_ALBUMS_LOAD_NEXT_SUCCESS,
  CLIENT_LIBRARY_ALBUMS_LOAD_SUCCESS,
  CLIENT_LIBRARY_ALBUMS_UPDATE,
  CLIENT_LIBRARY_ALBUMS_UPDATE_FAIL,
  CLIENT_LIBRARY_ALBUMS_UPDATE_SUCCESS,
  CLIENT_LIBRARY_FEATURED_ALBUMS_SET,
  CLIENT_LIBRARY_FEATURED_ALBUMS_SET_FAIL,
  CLIENT_LIBRARY_FEATURED_ALBUMS_SET_SUCCESS,
  CLIENT_LIBRARY_FEATURED_ALBUMS_UPDATE,
  CLIENT_LIBRARY_FEATURED_ALBUMS_UPDATE_FAIL,
  CLIENT_LIBRARY_FEATURED_ALBUMS_UPDATE_SUCCESS,
} from '@disco/constants/ActionTypes'
import { omit } from '@disco/utils/array'
import { pushUINotification } from '@disco/utils/notifications'
/**
 * Loads albums for a client library with optional search and featured filtering.
 * @param libraryId - ID of the client library to load albums for
 * @param query - Search query to filter albums
 * @param isFeatured - Optional flag to filter only featured albums
 * @returns Thunk that loads albums and handles success/error states
 */
export function clientLibraryAlbumsLoad(
  libraryId: ClientLibraryId,
  query: string,
  isFeatured?: boolean,
): Thunk {
  return async (dispatch, getState) => {
    dispatch({
      type: CLIENT_LIBRARY_ALBUMS_LOAD,
      libraryId,
    })

    try {
      const response = await api.getClientLibraryPlaylists(
        libraryId,
        'album',
        query,
        isFeatured,
      )
      mapResponseToReducerObjects(dispatch, getState, {
        ...response,
        results: response.results.map(item => omit(item, ['is_featured'])),
      })
      dispatch({
        type: CLIENT_LIBRARY_ALBUMS_LOAD_SUCCESS,
        libraryId,
        response,
      })
    } catch (error) {
      dispatch({
        type: CLIENT_LIBRARY_ALBUMS_LOAD_FAIL,
        libraryId,
        error,
      })
    }
  }
}
/**
 * Loads the next page of albums for a client library using pagination.
 * @param libraryId - ID of the client library to load next albums for
 * @param url - Pagination URL for the next page of albums
 * @returns Thunk that loads next page of albums
 */
export function clientLibraryAlbumsLoadNext(
  libraryId: ClientLibraryId,
  url: string,
): Thunk {
  return async (dispatch, getState) => {
    dispatch({
      type: CLIENT_LIBRARY_ALBUMS_LOAD_NEXT,
      libraryId,
    })

    try {
      const response = await api.getUrl<any>(
        url,
        null,
        'api.getClientLibraryAlbumsLoadNext',
      )
      mapResponseToReducerObjects(dispatch, getState, {
        ...response,
        results: response.results.map(item => omit(item, ['is_featured'])),
      })
      dispatch({
        type: CLIENT_LIBRARY_ALBUMS_LOAD_NEXT_SUCCESS,
        libraryId,
        response,
      })
    } catch (error) {
      dispatch({
        type: CLIENT_LIBRARY_ALBUMS_LOAD_NEXT_FAIL,
        libraryId,
        error,
      })
    }
  }
}
/**
 * Updates albums in a client library by adding and removing specified albums.
 * @param libraryId - ID of the client library to update
 * @param to_add - Array of album IDs to add to the library
 * @param to_remove - Array of album IDs to remove from the library
 * @returns Thunk that updates albums and shows success notification
 */
export function clientLibraryAlbumsUpdate(
  libraryId: ClientLibraryId,
  to_add: number[],
  to_remove: number[],
): Thunk {
  return async dispatch => {
    dispatch({
      type: CLIENT_LIBRARY_ALBUMS_UPDATE,
      libraryId,
      payload: {
        to_add,
        to_remove,
      },
    })

    try {
      await api.alterClientLibraryPlaylists(libraryId, to_add, to_remove)
      dispatch({
        type: CLIENT_LIBRARY_ALBUMS_UPDATE_SUCCESS,
        libraryId,
      })
      pushUINotification({
        message: 'Albums updated!',
        level: 'success',
      })
    } catch (error) {
      dispatch({
        type: CLIENT_LIBRARY_ALBUMS_UPDATE_FAIL,
        libraryId,
        error,
      })
    }
  }
}
/**
 * Updates featured albums in a client library by adding and removing specified albums.
 * @param libraryId - ID of the client library to update featured albums for
 * @param to_add - Array of album IDs to add to featured albums
 * @param to_remove - Array of album IDs to remove from featured albums
 * @returns Thunk that updates featured albums and shows success notification
 */
export function clientLibraryFeaturedAlbumsUpdate(
  libraryId: ClientLibraryId,
  to_add: number[],
  to_remove: number[],
): Thunk {
  return async dispatch => {
    dispatch({
      type: CLIENT_LIBRARY_FEATURED_ALBUMS_UPDATE,
      libraryId,
      payload: {
        to_add,
        to_remove,
      },
    })

    try {
      await api.alterClientLibraryFeaturedPlaylists(
        libraryId,
        to_add,
        to_remove,
      )
      dispatch({
        type: CLIENT_LIBRARY_FEATURED_ALBUMS_UPDATE_SUCCESS,
        libraryId,
      })
      pushUINotification({
        message: 'Albums updated!',
        level: 'success',
      })
    } catch (error) {
      dispatch({
        type: CLIENT_LIBRARY_FEATURED_ALBUMS_UPDATE_FAIL,
        libraryId,
        error,
      })
    }
  }
}
/**
 * Sets the complete list of featured albums for a client library.
 * @param libraryId - ID of the client library to set featured albums for
 * @param playlistIds - Array of album IDs to set as featured albums
 * @returns Thunk that sets featured albums and shows success notification
 */
export function clientLibraryFeaturedAlbumsSet(
  libraryId: ClientLibraryId,
  playlistIds: number[],
): Thunk {
  return async dispatch => {
    dispatch({
      type: CLIENT_LIBRARY_FEATURED_ALBUMS_SET,
      libraryId,
      playlistIds,
    })

    try {
      await api.setClientLibraryFeaturedPlaylists(libraryId, {
        album_ids: playlistIds,
      })
      dispatch({
        type: CLIENT_LIBRARY_FEATURED_ALBUMS_SET_SUCCESS,
        libraryId,
      })
      pushUINotification({
        message: 'Albums updated!',
        level: 'success',
      })
    } catch (error) {
      dispatch({
        type: CLIENT_LIBRARY_FEATURED_ALBUMS_SET_FAIL,
        libraryId,
        error,
      })
    }
  }
}
